import { type JSX, Show, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv } from '../lib/tv.ts'

/**
 * Empty, loading and error are not edge cases in an operations tool — a storekeeper meets the
 * empty state on their first morning and the error state whenever the warehouse Wi-Fi drops. All
 * three therefore get the same composed treatment rather than a bare string.
 *
 * `action` exists so an empty state can say what to do next instead of only that nothing is here.
 */
const state = tv({
  slots: {
    root: 'grid justify-items-center gap-2 border-y border-border bg-transparent px-control-x-lg py-8 text-center',
    title: 'text-ui font-medium text-text',
    description: 'max-w-[42ch] text-body text-text-subtle',
    action: 'mt-1',
    /* Deliberately quick. A slow spinner makes the same wait feel longer than it is. */
    spinner: 'size-4 animate-spin rounded-full border-2 border-border border-t-accent',
  },
})

interface StateProps extends Omit<JSX.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: string
  description?: string
  action?: JSX.Element
}

export type EmptyStateProps = StateProps
export type ErrorStateProps = StateProps
export type LoadingStateProps = StateProps

export const EmptyState = (props: EmptyStateProps) => {
  const [local, rest] = splitProps(props, ['title', 'description', 'action', 'class'])
  const styles = state()

  return (
    <div {...rest} class={cn(styles.root(), local.class)}>
      <p class={styles.title()}>{local.title}</p>
      <Show when={local.description}>
        {(description) => <p class={styles.description()}>{description()}</p>}
      </Show>
      <Show when={local.action}>{(action) => <div class={styles.action()}>{action()}</div>}</Show>
    </div>
  )
}

export const ErrorState = (props: ErrorStateProps) => {
  const [local, rest] = splitProps(props, ['title', 'description', 'action', 'class'])
  const styles = state()

  return (
    <div
      {...rest}
      class={cn(styles.root(), 'border-status-critical/40 bg-status-critical-soft', local.class)}
      role="alert"
    >
      <p class={cn(styles.title(), 'text-status-critical')}>{local.title}</p>
      <Show when={local.description}>
        {(description) => (
          <p class={cn(styles.description(), 'text-text-muted')}>{description()}</p>
        )}
      </Show>
      <Show when={local.action}>{(action) => <div class={styles.action()}>{action()}</div>}</Show>
    </div>
  )
}

export const LoadingState = (props: LoadingStateProps) => {
  const [local, rest] = splitProps(props, ['title', 'description', 'class'])
  const styles = state()

  return (
    <div {...rest} class={cn(styles.root(), local.class)} role="status" aria-live="polite">
      <span class={styles.spinner()} aria-hidden="true" />
      <p class={styles.title()}>{local.title}</p>
      <Show when={local.description}>
        {(description) => <p class={styles.description()}>{description()}</p>}
      </Show>
    </div>
  )
}

/**
 * Skeleton rows for a table or list that is loading in place. A layout-shaped placeholder beats a
 * spinner when the shape of the result is already known: the page does not jump when data lands.
 */
export interface SkeletonProps extends JSX.HTMLAttributes<HTMLDivElement> {
  rows?: number
}

export const Skeleton = (props: SkeletonProps) => {
  const [local, rest] = splitProps(props, ['rows', 'class'])

  return (
    <div
      {...rest}
      class={cn('grid gap-2', local.class)}
      role="status"
      aria-live="polite"
      aria-label="Завантаження"
    >
      {Array.from({ length: local.rows ?? 3 }, (_, index) => (
        <div
          class="h-8 rounded-control bg-surface-sunken"
          style={{
            animation: `pulse-soft 1.6s var(--ease-in-out) ${index * 90}ms infinite`,
          }}
        />
      ))}
    </div>
  )
}
